import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Reply, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const STATUSES = ["Open", "In Progress", "Resolved"];

const statusColor = {
  Open: "bg-cyan-400/15 text-cyan-300 border-cyan-400/30",
  "In Progress": "bg-amber-400/15 text-amber-300 border-amber-400/30",
  Resolved: "bg-emerald-400/15 text-emerald-300 border-emerald-400/30",
};

export default function SupportInbox() {
  const [messages, setMessages] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/support-messages").then(({ data }) => {
    setMessages(data);
    setSelectedId((cur) => cur || (data[0] && data[0].id) || null);
  });
  useEffect(() => { load(); }, []);

  const selected = messages.find((m) => m.id === selectedId);

  const setStatus = async (id, status) => {
    setMessages((ms) => ms.map((m) => (m.id === id ? { ...m, status } : m)));
    await api.patch(`/admin/support-messages/${id}`, { status });
    toast.success(`Marked as ${status}`);
  };

  const sendReply = async () => {
    if (!reply.trim()) return toast.error("Write a reply first");
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/support-messages/${selectedId}/reply`, { body: reply });
      toast.success(data.status === "pending" ? "Reply queued — it will send once a SendGrid key is set" : "Reply sent via email");
      setReply("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-[380px_1fr] gap-6 items-start" data-testid="admin-support-inbox">
      <div className="bg-[#0E1642] border border-white/10 rounded-2xl p-4 max-h-[75vh] overflow-y-auto">
        <h3 className="font-display font-bold text-white px-2 py-2">Support Inbox</h3>
        {messages.map((m) => (
          <button
            key={m.id}
            onClick={() => { setSelectedId(m.id); setReply(""); }}
            className={`w-full text-left rounded-xl p-4 mb-2 border transition-all duration-200 ${selectedId === m.id ? "bg-[#00BFFF]/10 border-cyan-400/40" : "border-white/5 hover:bg-white/[0.03]"}`}
            data-testid={`support-row-${m.id}`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="font-bold text-white text-sm truncate">{m.name}</span>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border flex-shrink-0 ${statusColor[m.status] || statusColor.Open}`}>{m.status}</span>
            </div>
            <div className="text-xs text-slate-300 truncate mt-1">{m.subject || "(no subject)"}</div>
            <div className="flex items-center justify-between mt-1.5">
              <span className="text-[10px] text-slate-500">{new Date(m.created_at).toLocaleDateString()}</span>
              <span className={`text-[10px] font-semibold ${m.type === "estimate" ? "text-cyan-300" : "text-slate-400"}`}>{m.type === "estimate" ? "Estimate request" : "Contact"}</span>
            </div>
          </button>
        ))}
        {messages.length === 0 && (
          <div className="py-10 text-center text-slate-500 text-sm px-4">No messages yet. Contact form and team estimate requests land here.</div>
        )}
      </div>

      <div className="bg-[#0E1642] border border-white/10 rounded-2xl p-6 sm:p-8 min-h-[300px]">
        {selected ? (
          <>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h3 className="font-display font-extrabold text-white text-xl">{selected.subject || "(no subject)"}</h3>
                <p className="text-sm text-slate-300 mt-1">
                  {selected.name} · <a href={`mailto:${selected.email}`} className="text-cyan-300 hover:underline">{selected.email}</a>
                </p>
                <p className="text-xs text-slate-500 mt-1">{new Date(selected.created_at).toLocaleString()}</p>
              </div>
              <select
                value={selected.status || "Open"}
                onChange={(e) => setStatus(selected.id, e.target.value)}
                className={`text-xs font-bold rounded-full px-3 py-1.5 border outline-none cursor-pointer ${statusColor[selected.status] || statusColor.Open}`}
                data-testid={`support-status-${selected.id}`}
              >
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div className="mt-6 rounded-xl border border-white/10 bg-[#0A0F2C]/50 p-5 text-sm text-slate-200 leading-relaxed whitespace-pre-line" data-testid="support-body">
              {selected.message}
            </div>

            {selected.last_reply_status && (
              <div className="mt-4 text-xs text-slate-400">
                Last reply: <span className={selected.last_reply_status === "sent" ? "text-emerald-300" : selected.last_reply_status === "pending" ? "text-amber-300" : "text-red-400"}>{selected.last_reply_status}</span>
                {selected.last_reply_at ? ` · ${new Date(selected.last_reply_at).toLocaleString()}` : ""}
              </div>
            )}

            <div className="mt-6">
              <div className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-2">Reply via email</div>
              <textarea
                className="admin-input"
                rows={4}
                placeholder={`Write your reply to ${selected.name}...`}
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                data-testid={`support-reply-${selected.id}`}
              />
              <button className="btn btn-sm btn-primary mt-3" onClick={sendReply} disabled={busy} data-testid="support-reply-send">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Reply size={14} />}
                {busy ? "Sending..." : "Send Reply"}
              </button>
            </div>
          </>
        ) : (
          <div className="py-16 text-center text-slate-500">Select a message to read and reply.</div>
        )}
      </div>
    </div>
  );
}
